import type { ActivityOrderRow } from "@/views/pages/productActivity/types";

import { fmtNum } from "../../tool";

type ActivityProductInfo = NonNullable<ActivityOrderRow["productInfo"]>;
type PreviewTab = "video" | "image";

export interface ActivityOrderPreviewPhoneProps {
    info: ActivityProductInfo;
    files: Record<number, string>;
    tab: PreviewTab;
    onTabChange: (tab: PreviewTab) => void;
}

const MARKET_ACTIVITY_PRICE = 1;
const MARKET_DROP = 2;

/** 活动订单交易快照：复刻 Vue PreviewPhone 的手机内容，不额外包裹 iPhone 外框。 */
export function ActivityOrderPreviewPhone({ info, files, tab, onTabChange }: ActivityOrderPreviewPhoneProps) {
    const imageIds = info.imgIds || [];
    const imageIndex = 0;
    const imageId = imageIds[imageIndex];
    const imageUrl = imageId == null ? undefined : files[imageId];
    const isMarketActivityPrice = info.pricingMethod === MARKET_ACTIVITY_PRICE;
    const isMarketDrop = info.pricingMethod === MARKET_DROP;

    // 交易快照默认打开视频页，与 Vue PreviewPhone 的初始 tab 保持一致。
    const renderMedia = () => {
        if (tab === "video") {
            // Vue 版即使 videoId 为空也保留原生 video 控件，保证空视频状态的画布高度和控件布局一致。
            return (
                <video
                    aria-label="交易快照视频"
                    className="activity-order-preview__video"
                    controls
                    role="video"
                    src={info.videoId == null ? undefined : files[info.videoId]}
                />
            );
        }

        return imageUrl ? (
            <img alt={`活动商品图片${imageIndex + 1}`} className="activity-order-preview__image" src={imageUrl} />
        ) : (
            <div className="activity-order-preview__empty">暂无图片</div>
        );
    };

    return (
        <div className="activity-order-preview" data-testid="activity-order-preview">
            <div className="activity-order-preview__media" data-testid="activity-order-preview-media">
                {renderMedia()}
                {tab === "video" && (!info.videoId || !files[info.videoId]) ? (
                    <span aria-hidden="true" className="activity-order-preview__video-loading" />
                ) : null}
                {tab === "image" && imageIds.length > 0 ? (
                    <span className="activity-order-preview__counter">
                        {imageIndex + 1}/{imageIds.length}
                    </span>
                ) : null}
                <div className="activity-order-preview__tabs">
                    <button
                        className={tab === "video" ? "is-active" : ""}
                        type="button"
                        onClick={() => onTabChange("video")}
                    >
                        视频
                    </button>
                    <button
                        className={tab === "image" ? "is-active" : ""}
                        type="button"
                        onClick={() => onTabChange("image")}
                    >
                        图片
                    </button>
                </div>
            </div>

            <div className="activity-order-preview__price">
                <div className="activity-order-preview__price-main">
                    <span className="activity-order-preview__price-label">活动单价¥</span>
                    <strong className="activity-order-preview__price-value">
                        {isMarketActivityPrice ? fmtNum(info.campaignUnitPrice) : "??"}
                    </strong>
                    <span className="activity-order-preview__unit">/吨</span>
                    <span className="activity-order-preview__discount">
                        {isMarketActivityPrice
                            ? `直降${fmtNum((info.unitPrice || 0) - (info.campaignUnitPrice || 0))}元/吨`
                            : isMarketDrop
                              ? `比市场价直降${fmtNum(info.lowerThanMarketPrice)}元/吨`
                              : "以活动现场公布为准"}
                    </span>
                </div>
                <div className="activity-order-preview__price-sub">
                    {isMarketActivityPrice ? (
                        <span>
                            市场价<span className="is-strikethrough">¥{fmtNum(info.unitPrice)}/吨</span>
                            <i aria-hidden="true">|</i>
                        </span>
                    ) : null}
                    <span>
                        限量{fmtNum(info.stock)}吨<i aria-hidden="true">|</i>
                    </span>
                    <span>起订吨数{fmtNum(info.minOrderTon)}吨</span>
                </div>
            </div>

            <div className="activity-order-preview__main">
                <div className="activity-order-preview__company">
                    <div className="activity-order-preview__company-name">{info.supplierEnterprise?.name || "-"}</div>
                    <div className="activity-order-preview__product">
                        <span>活动商品</span>
                        <span>{info.title || "-"}</span>
                    </div>
                </div>
                <div className="activity-order-preview__rule">
                    <div className="activity-order-preview__rule-title">活动规则</div>
                    <div className="activity-order-preview__rule-content">{info.campaignRule || ""}</div>
                </div>
            </div>
        </div>
    );
}

export default ActivityOrderPreviewPhone;
